import { FileText, Image as ImageIcon, Video } from "lucide-react";
import { useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { useMaterials } from "../materials/useMaterials";
import { useAgents } from "../useAgents";
import {
  BROADCAST_FOOTER,
  GAP_DAYS,
  INTERESTED_BUTTON,
  LEAD_STAGE_OPTIONS,
  OPT_OUT_BUTTON,
  TEMPERATURE_OPTIONS,
  bodyProblems,
  broadcastText,
  draftProblems,
  estimatedCost,
  type BroadcastDraft,
} from "./broadcasts";
import {
  invokeBroadcast,
  useAudienceCount,
  useSaveBroadcast,
  useZones,
} from "./useBroadcasts";

const KIND_ICON = { image: ImageIcon, video: Video, document: FileText };

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-full border px-2.5 py-1 text-xs transition-colors",
      active
        ? "border-lime-500 bg-lime-200 font-medium text-neutral-900"
        : "hover:bg-muted",
    )}
  >
    {children}
  </button>
);

const toggle = (list: string[] = [], value: string) =>
  list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];

/**
 * Prepares a broadcast: who gets it, what (video, PDF, photo or text) and when.
 * "Enviar a Meta" saves it and submits its template for approval; once Meta
 * approves it the CRM sends it by itself at the chosen time.
 */
export const BroadcastEditor = ({
  draft: initial,
  locked,
  onClose,
  onDone,
}: {
  draft: BroadcastDraft | null;
  /** Already submitted: only the date can change */
  locked?: boolean;
  onClose: () => void;
  onDone: () => void;
}) => {
  const notify = useNotify();
  const { meId } = useAgents();
  const { materials } = useMaterials();
  const zones = useZones();
  const save = useSaveBroadcast();
  const [draft, setDraft] = useState<BroadcastDraft | null>(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft(initial), [initial]);

  const count = useAudienceCount(
    draft?.segment ?? "leads",
    draft?.filters ?? {},
  );
  if (!draft) return null;

  const set = <K extends keyof BroadcastDraft>(
    key: K,
    value: BroadcastDraft[K],
  ) =>
    setDraft((previous) =>
      previous ? { ...previous, [key]: value } : previous,
    );
  const setFilter = (key: keyof BroadcastDraft["filters"], value: unknown) =>
    setDraft((previous) =>
      previous
        ? { ...previous, filters: { ...previous.filters, [key]: value } }
        : previous,
    );

  const material = materials.find(
    (item) => String(item.id) === String(draft.material_id),
  );
  const problems = [...draftProblems(draft), ...bodyProblems(draft.body)];
  const sendable = materials.filter((item) => item.active);

  const run = async (submit: boolean) => {
    setBusy(true);
    try {
      const id = await save(draft, meId);
      if (submit && !locked) {
        await invokeBroadcast("submit", id);
        notify("Enviada a Meta. Cuando la aprueben saldrá sola en su fecha", {
          type: "success",
        });
      } else {
        notify("Guardada", { type: "success" });
      }
      onDone();
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "No se ha podido guardar",
        { type: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{draft.id ? "Difusión" : "Nueva difusión"}</DialogTitle>
          <DialogDescription>
            Sale por WhatsApp desde el 614 como mensaje aprobado por Meta. Nadie
            recibe más de una cada {GAP_DAYS} días y quien pide la baja no
            vuelve a recibir ninguna.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-[1fr_300px]">
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="b-title">Nombre (solo lo veis vosotros)</Label>
                <Input
                  id="b-title"
                  value={draft.title}
                  disabled={locked}
                  onChange={(event) => set("title", event.target.value)}
                  placeholder="Ej.: Vídeo cómo comprar · Reactivar"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="b-when">Día y hora del envío</Label>
                <Input
                  id="b-when"
                  type="datetime-local"
                  value={draft.scheduled_at}
                  onChange={(event) => set("scheduled_at", event.target.value)}
                />
              </div>
            </div>

            <section className="flex flex-col gap-2 rounded-2xl border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium">Para quién</span>
                <span className="text-sm">
                  {count == null ? (
                    "Contando…"
                  ) : (
                    <>
                      <strong>{count}</strong> personas · unos{" "}
                      {estimatedCost(count)}
                    </>
                  )}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Chip
                  active={draft.segment === "leads"}
                  onClick={() => !locked && set("segment", "leads")}
                >
                  Leads (todos menos quien compró)
                </Chip>
                <Chip
                  active={draft.segment === "clients"}
                  onClick={() => !locked && set("segment", "clients")}
                >
                  Clientes que ya compraron
                </Chip>
              </div>
              {draft.filters.group?.key ? (
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-fuchsia-300 bg-fuchsia-50 px-3 py-2 text-sm dark:border-fuchsia-800 dark:bg-fuchsia-950/30">
                  <span>
                    Grupo de Reactivación:{" "}
                    <strong>
                      {draft.filters.group.label ?? draft.filters.group.key}
                    </strong>
                  </span>
                  {!locked ? (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground underline"
                      onClick={() => setFilter("group", null)}
                    >
                      Quitar
                    </button>
                  ) : null}
                </div>
              ) : null}
              {draft.segment === "leads" ? (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">
                    Fases (ninguna marcada = todas)
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {LEAD_STAGE_OPTIONS.map((option) => (
                      <Chip
                        key={option.value}
                        active={!!draft.filters.stages?.includes(option.value)}
                        onClick={() =>
                          !locked &&
                          setFilter(
                            "stages",
                            toggle(draft.filters.stages, option.value),
                          )
                        }
                      >
                        {option.label}
                      </Chip>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">
                  Temperatura
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {TEMPERATURE_OPTIONS.map((option) => (
                    <Chip
                      key={option.value}
                      active={
                        !!draft.filters.temperatures?.includes(option.value)
                      }
                      onClick={() =>
                        !locked &&
                        setFilter(
                          "temperatures",
                          toggle(draft.filters.temperatures, option.value),
                        )
                      }
                    >
                      {option.label}
                    </Chip>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Zona</span>
                <div className="flex flex-wrap gap-1.5">
                  {zones.map((zone) => (
                    <Chip
                      key={zone}
                      active={!!draft.filters.zones?.includes(zone)}
                      onClick={() =>
                        !locked &&
                        setFilter("zones", toggle(draft.filters.zones, zone))
                      }
                    >
                      {zone === "none" ? "Sin zona" : zone}
                    </Chip>
                  ))}
                </div>
              </div>
              <label className="flex flex-wrap items-center gap-2 text-sm">
                Solo los que nadie ha tocado en
                <Input
                  type="number"
                  min={0}
                  className="h-8 w-20"
                  disabled={locked}
                  value={draft.filters.quiet_days ?? ""}
                  onChange={(event) =>
                    setFilter(
                      "quiet_days",
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
                días (vacío = da igual)
              </label>
            </section>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="b-material">Archivo (vídeo, PDF o foto)</Label>
              <select
                id="b-material"
                className="h-9 rounded-md border bg-background px-2 text-sm"
                value={
                  draft.material_id == null ? "" : String(draft.material_id)
                }
                disabled={locked}
                onChange={(event) =>
                  set(
                    "material_id",
                    event.target.value ? Number(event.target.value) : null,
                  )
                }
              >
                <option value="">Sin archivo (solo texto)</option>
                {sendable.map((item) => (
                  <option key={String(item.id)} value={String(item.id)}>
                    {item.title}
                  </option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">
                Sale de Material. Para uno nuevo, súbelo primero en Mensajes →
                Material.
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="b-body">Texto</Label>
              <Textarea
                id="b-body"
                rows={7}
                value={draft.body}
                disabled={locked}
                onChange={(event) => set("body", event.target.value)}
              />
              <span className="text-xs text-muted-foreground">
                Escribe {"{nombre}"} donde va el nombre del lead. Meta lo revisa
                antes de enviarlo (de minutos a 24 h): prográmala con margen.
              </span>
            </div>

            {problems.length && !locked ? (
              <ul className="list-disc rounded-xl bg-amber-50 py-2 pl-6 pr-3 text-xs text-amber-900 dark:bg-amber-400/10 dark:text-amber-200">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Así le llega</span>
            <div className="rounded-2xl bg-[#e7ddd3] p-3 dark:bg-neutral-800">
              <div className="flex flex-col overflow-hidden rounded-xl bg-white text-sm text-neutral-900 shadow-sm dark:bg-neutral-900 dark:text-neutral-100">
                {material ? (
                  material.kind === "image" ? (
                    <img
                      src={material.url}
                      alt=""
                      className="max-h-48 w-full object-cover"
                    />
                  ) : material.kind === "video" ? (
                    <video
                      src={material.url}
                      className="max-h-48 w-full"
                      muted
                    />
                  ) : (
                    <div className="flex items-center gap-2 bg-neutral-100 p-3 text-xs dark:bg-neutral-800">
                      {(() => {
                        const Icon = KIND_ICON[material.kind];
                        return <Icon className="h-4 w-4" />;
                      })()}
                      {material.file_name}
                    </div>
                  )
                ) : null}
                <p className="whitespace-pre-wrap px-3 pt-2">
                  {broadcastText(draft.body, "María")}
                </p>
                <p className="px-3 pb-2 pt-1 text-xs text-neutral-500">
                  {BROADCAST_FOOTER}
                </p>
                <div className="grid border-t text-center text-sm text-sky-700 dark:text-sky-300">
                  <span className="border-b py-1.5">{INTERESTED_BUTTON}</span>
                  <span className="py-1.5">{OPT_OUT_BUTTON}</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              «Me interesa» o cualquier respuesta de un lead en Reactivar o No
              compra lo devuelve a Nuevo para los setters, con aviso en el chat.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="outline"
            disabled={busy || (!locked && !draft.title.trim())}
            onClick={() => run(false)}
          >
            {locked ? "Guardar fecha" : "Guardar borrador"}
          </Button>
          {!locked ? (
            <Button
              disabled={busy || problems.length > 0}
              onClick={() => run(true)}
            >
              Enviar a Meta para aprobar
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
